import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { contactService } from '../../services/contactService';
import { Save, X } from 'lucide-react';

const ContactContentManager = ({ setEditingContact, updatePageLastUpdated }) => {
  const [contactContent, setContactContent] = useState(null);
  const [loadingContactContent, setLoadingContactContent] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContactPageContent = async () => {
      setLoadingContactContent(true);
      try {
        const content = await contactService.getContactContent();
        setContactContent(content);
      } catch (error) {
        showError('Failed to load Contact page content: ' + error.message, 'error');
        setContactContent(null);
      } finally {
        setLoadingContactContent(false);
      }
    };
    fetchContactPageContent();
  }, [showError]);

  const handleSaveContactContent = async (sectionName, content) => {
    try {
      await contactService.updateContactSection(sectionName, content);
      showError(`${sectionName} section updated successfully!`, 'success');
      const updatedContent = await contactService.getContactContent();
      setContactContent(updatedContent);
      await updatePageLastUpdated('Contact');
    } catch (error) {
      showError(`Failed to update ${sectionName} section: ${error.message}`, 'error');
    }
  };

  const handleContactContentChange = (field, value) => {
    setContactContent(prevContent => ({
      ...prevContent,
      [field]: value
    }));
  };

  const handleContactInfoChange = (field, value) => {
    setContactContent(prevContent => ({
      ...prevContent,
      contact_info: {
        ...prevContent.contact_info,
        [field]: value
      }
    }));
  };

  const handleCtaContentChange = (field, value) => {
    setContactContent(prevContent => ({
      ...prevContent,
      [field]: value
    }));
  };

  if (loadingContactContent) {
    return (
      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Contact page editor...</p>
        </div>
      </div>
    );
  }

  if (!contactContent) {
    return (
      <div className="text-center py-8 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg shadow-md">
        <p className="font-semibold">Error:</p>
        <p>Could not load Contact content for editing. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
      <div className="flex justify-between items-center mb-6 border-b pb-4 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Contact Page Content</h2>
        <button
          onClick={() => setEditingContact(false)}
          className="flex items-center md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 text-sm font-medium shadow-sm"
        >
          <X className="w-4 h-4 mr-2" /> Close
        </button>
      </div>

      <div className="space-y-8">
        {/* Main Content */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Main Content</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="main_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heading</label>
              <input
                type="text"
                id="main_heading"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.main_heading || ''}
                onChange={(e) => handleContactContentChange('main_heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="main_subheading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sub-heading</label>
              <input
                type="text"
                id="main_subheading"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.main_subheading || ''}
                onChange={(e) => handleContactContentChange('main_subheading', e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContactContent('main', { main_heading: contactContent.main_heading, main_subheading: contactContent.main_subheading, contact_info: contactContent.contact_info })}
            className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Save Main Content
          </button>
        </div>

        {/* Contact Info */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
              <textarea
                id="address"
                rows="3"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.contact_info?.address || ''}
                onChange={(e) => handleContactInfoChange('address', e.target.value)}
              ></textarea>
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input
                type="text"
                id="phone"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.contact_info?.phone || ''}
                onChange={(e) => handleContactInfoChange('phone', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                id="email"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.contact_info?.email || ''}
                onChange={(e) => handleContactInfoChange('email', e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContactContent('contact_info', contactContent.contact_info)}
            className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Save Contact Info
          </button>
        </div>
        
        {/* Map Section */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Map Section</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="map_embed_url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Google Maps Embed URL</label>
              <input
                type="text"
                id="map_embed_url"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.map_embed_url || ''}
                onChange={(e) => handleContactContentChange('map_embed_url', e.target.value)}
              />
            </div>
            {contactContent?.map_embed_url && (
              <div className="mt-4">
                <h4 className="text-md font-semibold text-gray-800 dark:text-white mb-2">Map Preview:</h4>
                <div className="w-full h-64 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src={contactContent.map_embed_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Google Map"
                  ></iframe>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => handleSaveContactContent('map_embed', {map_embed_url: contactContent.map_embed_url})}
            className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Save Map
          </button>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Call to Action Section</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="cta_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heading</label>
              <input
                type="text"
                id="cta_heading"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.cta_heading || ''}
                onChange={(e) => handleCtaContentChange('cta_heading', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="cta_subheading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subheading</label>
              <textarea
                id="cta_subheading"
                rows="3"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.cta_subheading || ''}
                onChange={(e) => handleCtaContentChange('cta_subheading', e.target.value)}
              ></textarea>
            </div>
            <div>
              <label htmlFor="cta_button_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Button Text</label>
              <input
                type="text"
                id="cta_button_text"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.cta_button_text || ''}
                onChange={(e) => handleCtaContentChange('cta_button_text', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="cta_button_link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Button Link</label>
              <input
                type="text"
                id="cta_button_link"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                value={contactContent?.cta_button_link || ''}
                onChange={(e) => handleCtaContentChange('cta_button_link', e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => handleSaveContactContent('cta', {
              cta_heading: contactContent.cta_heading,
              cta_subheading: contactContent.cta_subheading,
              cta_button_text: contactContent.cta_button_text,
              cta_button_link: contactContent.cta_button_link,
            })}
            className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Save CTA Section
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactContentManager;